import { Accordion, Anchor, Loader, Modal, Table, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import type { TeamDailyKennzahlTicket } from "@msc/shared";

import { formatBerlinDate } from "../../lib/date.js";

/**
 * Die Ticketliste hinter einer Zahl oder einem Balken. Eine Liste statt eines
 * Board-Filters: Das Board zeigt nur „In Progress", viele gezählte Tickets
 * stünden dort gar nicht (Spec, Lieferung A).
 */
export function TicketListeModal({
  titel,
  tickets,
  jiraBaseUrl,
  opened,
  onClose,
  spalteSeit = false,
  laedt = false,
  fehler = null,
  abschnitte,
  ohneBearbeiter = false,
  spalteGeaendert = false,
  hinweis = null,
}: {
  titel: string;
  tickets: TeamDailyKennzahlTicket[];
  jiraBaseUrl: string | null;
  opened: boolean;
  onClose: () => void;
  /** Spalte „ausgesetzt seit" statt „angelegt". */
  spalteSeit?: boolean;
  /** Die Liste wird noch geholt: Loader statt „Keine Tickets.“. */
  laedt?: boolean;
  /** Das Holen ist gescheitert: Meldung statt „Keine Tickets.“. */
  fehler?: string | null;
  /** Getrennte Abschnitte (z. B. Erstellt/Gelöst); ersetzt `tickets`, wenn gegeben. */
  abschnitte?: { titel: string; tickets: TeamDailyKennzahlTicket[] }[];
  /** Spalte „Bearbeiter" ausblenden (der Spiegel kennt keinen Bearbeiter). */
  ohneBearbeiter?: boolean;
  /** Spalte „Letzte Änderung" statt „angelegt". */
  spalteGeaendert?: boolean;
  /** Kleiner Hinweis über der Liste, z. B. „mehr als 500, die ältesten gezeigt". */
  hinweis?: string | null;
}) {
  const { t } = useTranslation();
  const basis = jiraBaseUrl?.replace(/\/+$/, "") ?? null;
  const tabelle = (liste: TeamDailyKennzahlTicket[]) => (
    <Table.ScrollContainer minWidth={560}>
      <Table striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("pages.teamDaily.kennzahlen.spalteTicket")}</Table.Th>
            <Table.Th>{t("pages.teamDaily.kennzahlen.spalteKunde")}</Table.Th>
            <Table.Th>{t("pages.teamDaily.kennzahlen.spalteStatus")}</Table.Th>
            {!ohneBearbeiter && <Table.Th>{t("pages.teamDaily.kennzahlen.spalteBearbeiter")}</Table.Th>}
            <Table.Th>
              {t(
                spalteSeit
                  ? "pages.teamDaily.kennzahlen.spalteSeit"
                  : spalteGeaendert
                    ? "pages.teamDaily.kennzahlen.spalteGeaendert"
                    : "pages.teamDaily.kennzahlen.spalteAngelegt",
              )}
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {liste.map((x) => (
            <Table.Tr key={x.key}>
              <Table.Td>
                {basis ? (
                  <Anchor href={`${basis}/browse/${x.key}`} target="_blank" rel="noopener noreferrer">
                    {x.key}
                  </Anchor>
                ) : (
                  x.key
                )}{" "}
                <Text span size="sm">
                  {x.summary}
                </Text>
              </Table.Td>
              <Table.Td>{x.customerLabel ?? "–"}</Table.Td>
              <Table.Td>{x.status ?? "–"}</Table.Td>
              {!ohneBearbeiter && (
                <Table.Td>{x.assigneeName ?? t("pages.teamDaily.kennzahlen.ohneBearbeiterKurz")}</Table.Td>
              )}
              <Table.Td>
                {spalteSeit
                  ? x.ausgesetztSeit
                    ? formatBerlinDate(x.ausgesetztSeit)
                    : t("pages.teamDaily.kennzahlen.seitUnbekannt")
                  : spalteGeaendert
                    ? x.updated
                      ? formatBerlinDate(x.updated)
                      : "–"
                    : formatBerlinDate(x.created)}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
  return (
    <Modal opened={opened} onClose={onClose} title={titel} size="xl">
      {hinweis && (
        <Text size="xs" c="dimmed" mb="xs">
          {hinweis}
        </Text>
      )}
      {laedt ? (
        <Loader size="sm" aria-label={t("pages.teamDaily.kennzahlen.listeLaedt")} />
      ) : fehler ? (
        <Text size="sm" c="red">
          {fehler}
        </Text>
      ) : (abschnitte ? abschnitte.every((a) => a.tickets.length === 0) : tickets.length === 0) ? (
        <Text size="sm" c="dimmed">
          {t("pages.teamDaily.kennzahlen.listeLeer")}
        </Text>
      ) : (
        abschnitte && abschnitte.length > 1 ? (
          // Mehrere lange Listen untereinander sind unübersichtlich: Die Titel (mit Anzahl) sind
          // Knöpfe, alles startet zugeklappt.
          <Accordion multiple variant="separated">
            {abschnitte.map((a) => (
              <Accordion.Item key={a.titel} value={a.titel}>
                <Accordion.Control>
                  <Text fw={700} size="sm">
                    {a.titel}
                  </Text>
                </Accordion.Control>
                <Accordion.Panel>
                  {a.tickets.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      {t("pages.teamDaily.kennzahlen.listeLeer")}
                    </Text>
                  ) : (
                    tabelle(a.tickets)
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        ) : abschnitte ? (
          abschnitte.map((a) => (
            <div key={a.titel}>
              <Text fw={700} size="sm" mt="xs" mb={4}>
                {a.titel}
              </Text>
              {a.tickets.length === 0 ? (
                <Text size="sm" c="dimmed">
                  {t("pages.teamDaily.kennzahlen.listeLeer")}
                </Text>
              ) : (
                tabelle(a.tickets)
              )}
            </div>
          ))
        ) : (
          tabelle(tickets)
        )
      )}
    </Modal>
  );
}
